<script setup>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { formatDateLocale } from '@/libs/utils/date.js'
import { getWorkflows } from '@/services/workflowService.js'
import { getCampaigns, getColumns } from '@/services/campaignService.js'

import Paginazione from '@/components/Generics/Paginations/Paginazione.vue'

import ConfigurationProvider from '@/provider/configuration.js'
import { client } from '@/services/fetch.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faKey, faPencil, faTrash, faPlus, faEye } from '@fortawesome/free-solid-svg-icons'
import { toast } from '@/services/toast.js'
import { usePageNumberStore } from '@/services/store.js'

library.add(faPencil, faKey, faTrash, faPlus, faEye)

const pageNumberStore = usePageNumberStore()
const route = useRoute()
const campaigns = ref({
  data: [],
  pagination: {},
  columns: []
})
const isLoading = ref(true)
const currentPage = ref(1)
const networkError = ref({
  enabled: false,
  message: 'Errore nel caricamento dei dati, riprova più tardi'

})
const deleteAlert = ref({
  enabled: false,
  message: 'La campagna è stata eliminata con successo'
})
const workflowLabels = ref({})
const showModal = ref(false)
const campaignToDelete = ref(null)
const campaignType = ref(ConfigurationProvider.getConfig().campaign.campaignType)
const canView = ref(false)

onMounted(async () => {
  canView.value = route.meta?.permissions?.canView ?? false
  if (!canView.value) {
    return
  }

  if (route.query.page) {
    currentPage.value = route.query.page
  }

  campaigns.value.columns = await getColumns()
  await loadCampaigns(pageNumberStore.getPage(route.name))

  workflowLabels.value = await getWorkflows('campaign').then((result) => {
    return result.reduce((acc, item) => {
      acc[item.key] = item.label
      return acc
    }, {})
  })
})

const loadCampaigns = async (page) => {
  try {
    const campagne = await getCampaigns(page, campaignType.value)

    campaigns.value.data = campagne.data
    campaigns.value.pagination = campagne.pagination
    isLoading.value = false
  } catch (error) {
    networkError.value.enabled = true
    isLoading.value = false
  }
}

const changePage = async (page) => {
  isLoading.value = true
  currentPage.value = page
  await loadCampaigns(page)
  isLoading.value = false
}

const confirmDelete = (campaignId) => {
  campaignToDelete.value = campaignId
  showModal.value = true
}

const deleteCampaign = async () => {
  try {
    networkError.value.enabled = false
    deleteAlert.value.enabled = false
    let url = `/campaign/api/campaign/${campaignToDelete.value}`
    await client.delete(url)
    showModal.value = false
    campaignToDelete.value = null
    await loadCampaigns(currentPage.value)
    if (campaigns.value.data.length === 0 && currentPage.value > 1) {
      currentPage.value--
      isLoading.value = true
      await loadCampaigns(currentPage.value)
    }

    deleteAlert.value.enabled = true
    toast.create('Campagna eliminata con successo', 'success')
  } catch (error) {
    showModal.value = false
    networkError.value.enabled = true
    campaignToDelete.value = null
    toast.create('Qualcosa è andato storto, riprova più tardi', 'error')
  }
}

</script>

<template>
  <div class="container-fluid px-0">
    <div class="card">
      <div class="card-body px-0">

        <div
          v-if="isLoading"
          class="d-flex justify-content-center "
        >
          <div class="spinner-grow " role="status">
            <span class="sr-only">Loading...</span>
          </div>

        </div>

        <div v-else class="table-responsive list mt-1">
          <div v-if="campaigns.data.length === 0" class="alert alert-warning mt-2" role="alert">
            Nessuna campagna disponibile.
          </div>
          <table class="table table-striped">
            <thead>
            <tr>
              <th class="border-top-0" v-for="item in campaigns.columns" :key="item.field">{{ item.label }}</th>
              <th class="border-top-0">Azioni</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="campaign in campaigns.data" :key="campaign.id">
              <td v-for="item in campaigns.columns" :key="item.id">
                <span v-if="item.field === 'poll.title'">{{ campaign.poll.title }}</span>

                <span v-else-if="item.type === 'date' || item.type === 'datetime'">
                  {{ formatDateLocale(campaign[item.field], item.type === 'datetime') }}
                </span>

                <span v-else-if="item.field === 'pollTemplateRevisionNumber'">
                   {{
                    campaign['pollTemplateRevisionNumber']
                  }}
                </span>

                <span v-else-if="item.field === 'workflow'">
                  {{ workflowLabels[campaign.workflow] || campaign.workflow }}
                </span>

                <span v-else-if="item.field === 'state' && campaign['stateTranslated']">
                  {{ campaign['stateTranslated'] }}
                </span>

                <span v-else>{{ campaign[item.field] }}</span>
              </td>
              <td>
                <div class="d-flex justify-content-end">

                  <router-link
                    :to="{name: 'admin/DefaultVr_Campaign--detail', params: {'id': campaign.id}, query: {page: campaigns.pagination.page}}"
                  >
                    <button v-if="true" class="btn btn-primary btn-sm" title="Visualizza campagna">
                      <font-awesome-icon icon=" fa-eye" />
                    </button>
                  </router-link>

                  <router-link
                    v-if="campaign.enabledActions.update"
                    :to="{name: 'admin/DefaultVr_Campaign--edit', params: {'id': campaign.id}, query: {page: campaigns.pagination.page}}"
                  >
                    <button v-if="true" class="btn btn-primary btn-sm ms-1" title="Modifica campagna">
                      <font-awesome-icon icon=" fa-pencil" />
                    </button>
                  </router-link>

                  <router-link
                    v-if="campaign.enabledActions.permissions"
                    :to="{name: 'admin/DefaultVr_Permissions', params: {'id': campaign.id, type: 'campaign'}, query: {page: campaigns.pagination.page}}"
                  >
                    <button class="btn btn-primary btn-sm ms-1" title="Assegna permessi">
                      <font-awesome-icon icon=" fa-key" />
                    </button>
                  </router-link>

                  <button
                    v-if="campaign.enabledActions.delete"
                    @click="confirmDelete(campaign.id)" class="btn btn-danger btn-sm ms-1"
                    title="Elimina Campagna">
                    <font-awesome-icon icon=" fa-trash" />
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex justify-content-end m-2" v-if="!isLoading">
          <Paginazione v-if="!networkError.enabled"
                       :total-pages="
                Math.ceil(campaigns.pagination.total_items / campaigns.pagination.items_per_page)
              "
                       @page-changed="changePage"
          />
        </div>
        <transition name="modal">
          <div v-if="showModal" class="modal fade show d-block mt-5" tabindex="-1" @click.self="showModal = false">
            <div class="modal-dialog">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Conferma Eliminazione</h5>
                  <button type="button" class="btn-close" @click="showModal = false" aria-label="Close">
                  </button>
                </div>
                <div class="modal-body">
                  <p>Sei sicuro di voler eliminare questa campagna?</p>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" @click="showModal = false">Annulla</button>
                  <button type="button" class="btn btn-danger" @click="deleteCampaign">Elimina</button>
                </div>
              </div>
            </div>
          </div>
        </transition>
        <transition name="modal-backdrop">
          <div v-if="showModal" class="modal-backdrop fade show"></div>
        </transition>

      </div>
    </div>
  </div>
</template>

<style lang="scss">
$font-size-base: 1rem;
.container-fluid,
.modal {
  font-family: var(--bs-font-sans-serif), serif;
  font-size: $font-size-base;

  & .table-responsive {
    overflow-y: visible;
  }

  & .vs__search {
    font-size: 1rem;
  }

  & .vs__dropdown-toggle {
    font-size: 1rem;
  }

  & .vs__dropdown-toggle, .vs__dropdown-toggle {
    border-color: #dee2e6;
  }
}

.fa-pencil, .fa-key, .fa-trash {
  width: 1rem;
}

.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-backdrop-enter-active, .modal-backdrop-leave-active {
  transition: opacity 0.3s ease;
}

.modal-backdrop-enter-from, .modal-backdrop-leave-to {
  opacity: 0;
}

</style>
